import { useQuery } from "react-query";
import { useAgentsHttp } from "../useAgentsHttp";
import { QK } from "../queryKeys";
import type { CostKind } from "./useCostSummary";

// Mirrors the BE route ``POST /cost/line-items`` - server-side paginated +
// sorted itemisation of every priced Bedrock call across all four cost
// sources. The AI Costs page drives this off its date range plus its own
// page/sort/filter controls; paging happens on the BE so a 30-day window
// with thousands of calls never lands as one giant client-side list.
// All amounts are USD.

export interface CostLineItem {
  id: string;
  kind: CostKind;
  occurred_at: string; // ISO timestamp
  project_id: string | null;
  // The agent-config slot that ran it; null outside an agent run.
  config?: string | null;
  // "<name> (deleted)" when the owning project has been deleted (BE-stamped on
  // the ledger row); null for a live project, whose name resolves FE-side.
  deleted_project_label?: string | null;
  // A row is one logical run, which can span multiple models (throttle
  // fallback / a cheaper-model tool call). ``model_id`` is the sole model when
  // there's exactly one, else null; ``models`` is the full set the run used.
  model_id: string | null;
  models: string[] | null;
  // Per-model token share of the run, sorted by tokens DESC, each
  // {model_id, tokens, pct (of the run's total tokens)}. Drives the Model
  // column's "75% opus, 25% haiku" rendering + the trace modal's totals header.
  model_usage: { model_id: string | null; tokens: number; pct: number }[] | null;
  // Optional free-text note appended after the model id in the Model column
  // (e.g. "See PR #314 for full details" on a consolidated operational row).
  // null for normal per-run rows.
  model_note?: string | null;
  // null when the BE couldn't price the run (unknown model); ``unpriced``
  // is the explicit flag the UI keys off rather than the null amount.
  cost_usd: number | null;
  unpriced: boolean;
  // Web searches this run issued (billed per query, no tokens). cost_usd above
  // already includes the search cost; these surface the count + isolated cost.
  web_search_count?: number;
  // Seconds of Slack voice-note audio transcribed (no tokens, so no model).
  transcription_seconds?: number;
  web_search_cost_usd?: number | null;
  // The agent run this spend belongs to (a conversation turn or a scheduled pass),
  // so the row can open it; null for spend that is not a run's.
  run_id?: string | null;
  // (surface, artefactId) addressing the persisted AI trace - fed straight
  // into <ViewAiTraceButton>. null for sources with no trace artefact
  // (gmail_sync / admin_health / unattributed).
  trace_surface: string | null;
  trace_artefact_id: string | null;
  // Whether a persisted AI trace actually exists for this line item's
  // source row (BE ``has_ai_trace`` flag). False for rows produced before
  // trace capture -> the "Trace" button is hidden.
  has_trace: boolean;
  // Whether this AI invocation FAILED (a hard error: an agent run persisted via
  // the cost-on-failure path, or a failed extraction). Drives the row's failed
  // marker; the page's status filter maps to the BE ``status`` knob.
  failed: boolean;
}

export type CostLineItemsSort = "date" | "cost";
export type CostLineItemsOrder = "asc" | "desc";
// Outcome filter: "all" (default), only "succeeded", or only "failed" calls.
export type CostLineItemsStatus = "all" | "succeeded" | "failed";

export interface CostLineItemsParams {
  // Range: either an explicit calendar window or a trailing N-day window,
  // mirroring /cost/summary. The page always passes start/end.
  start?: string;
  end?: string;
  days?: number;
  kinds?: CostKind[];
  project_id?: string;
  config?: string;
  status?: CostLineItemsStatus; // default "all"
  sort?: CostLineItemsSort; // default "date"
  order?: CostLineItemsOrder; // default "desc"
  page?: number; // 1-based, default 1
  page_size?: number; // default 100, 1..500
}

export interface CostLineItemsResponse {
  items: CostLineItem[];
  total: number;
  page: number;
  page_size: number;
  sort: CostLineItemsSort;
  order: CostLineItemsOrder;
  status: CostLineItemsStatus;
}

const QUERY_KEY = QK.costLineItems;

export const useCostLineItems = (params: CostLineItemsParams) => {
  const http = useAgentsHttp();

  const {
    start,
    end,
    days,
    kinds,
    project_id,
    config,
    status = "all",
    sort = "date",
    order = "desc",
    page = 1,
    page_size = 100,
  } = params;

  // The explicit range needs both ends; until both are set we don't fire
  // (avoids a 400 round-trip on a half-set range). A trailing-days request
  // is always well-formed.
  const hasRange = Boolean(start && end);
  const enabled = hasRange || typeof days === "number";

  // The query key carries EVERY request input so each page / sort / filter /
  // range combination caches independently - paging back and forth is then a
  // cache hit, not a refetch. kinds is sorted+joined for a stable key
  // regardless of selection order.
  const kindsKey = kinds && kinds.length ? [...kinds].sort().join(",") : null;

  return useQuery<CostLineItemsResponse>(
    [
      QUERY_KEY,
      start ?? null,
      end ?? null,
      days ?? null,
      kindsKey,
      project_id ?? null,
      config ?? null,
      status,
      sort,
      order,
      page,
      page_size,
    ],
    async () =>
      http<CostLineItemsResponse>("/cost/line-items", {
        ...(hasRange ? { start, end } : days ? { days } : {}),
        ...(kinds && kinds.length ? { kinds } : {}),
        ...(project_id ? { project_id } : {}),
        ...(config ? { config } : {}),
        ...(status !== "all" ? { status } : {}),
        sort,
        order,
        page,
        page_size,
      }),
    {
      enabled,
      // Smooth paging: hold the previous page's rows on screen while the
      // next page loads instead of flashing an empty table.
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  );
};
